/**
 * progress-tracker.ts — Persists review progress to persistent storage
 *
 * Tracks user's position in the document review process so they can
 * resume from where they left off when reopening the document.
 *
 * Uses OfficeRuntime.storage for cross-platform compatibility (including Mac).
 */

import {
  getDocumentIdentifier,
  getDocumentMetadata,
  clearDocumentCache,
} from "../services/document-services";
import {
  loadDocumentSetting,
  removeDocumentSetting,
  saveDocumentSettings,
  setDocumentSetting,
  REVIEW_PROGRESS_SETTING_KEY,
} from "src/services/settings-service";
import { storage } from "./storage-adapter";

import type { CharStyleFormattingOrigin } from "src/services/character_styles/types";
import type { OutlineMode } from "src/types";

// Re-export clearDocumentCache for backward compatibility
export { clearDocumentCache };

const STORAGE_KEY_PREFIX = "mss_review_progress_";
const STORAGE_VERSION = 5; // Simplified to only store paragraphDocIndex

export interface ReviewProgress extends ReviewProgressFields {
  documentId: string;
  documentName: string;
  lastSaved: string;
  version: number;
}

export interface ReviewProgressFields {
  boundaryIndices?: [number, number] | null;
  outlineMode?: OutlineMode;
  profileName?: string | null;
  selectedCharacterStyleFilter?: string | null;
  selectedCharacterOriginFilter?: CharStyleFormattingOrigin | null;
  selectedCharacterStyleId?: string | null;
  selectedGroupFilter?: string | null;
  selectedIndices?: [number, number?] | null;
  selectedStyleFilter?: string | null;
  total?: number | null;
}

let saveQueue: Promise<void> = Promise.resolve();
let pendingFields: ReviewProgressFields | null = null;
let pendingSave: Promise<void> = Promise.resolve();

/**
 * Save review progress to localStorage.
 *
 * Writes are serialized because each one merges over the previously saved
 * state.  A single outline click saves twice (the selected group and its
 * chapter boundaries); run concurrently, both would read the same base and the
 * slower write would restore the other's field to its previous value.
 *
 * Calls made while a write is still queued fold into it, so rapid navigation
 * doesn't stack up one slow document-settings save per click.
 */
export function saveReviewProgress(fields: ReviewProgressFields): Promise<void> {
  if (pendingFields) {
    for (const [key, value] of Object.entries(fields)) {
      if (value !== undefined) (pendingFields as Record<string, unknown>)[key] = value;
    }
    return pendingSave;
  }
  pendingFields = { ...fields };
  // `writeReviewProgress` never rejects, so the chain cannot be poisoned.
  saveQueue = saveQueue.then(() => {
    const queued = pendingFields as ReviewProgressFields;
    pendingFields = null;
    return writeReviewProgress(queued);
  });
  pendingSave = saveQueue;
  return pendingSave;
}

/**
 * Optimized to fetch document metadata in a single Word API call.
 */
async function writeReviewProgress({
  boundaryIndices,
  outlineMode,
  profileName,
  selectedCharacterStyleFilter,
  selectedCharacterOriginFilter,
  selectedCharacterStyleId,
  selectedGroupFilter,
  selectedIndices,
  selectedStyleFilter,
  total,
}: ReviewProgressFields): Promise<void> {
  try {
    const { documentId, documentName } = await getDocumentMetadata();
    // Prefer the document-settings copy as the merge base — it's the
    // durable, cross-session source of truth. OfficeRuntime.storage is
    // local to this device and comes back empty after reopening the
    // document elsewhere, which would otherwise wipe out filters that
    // were just restored from the document.
    const reviewProgress = loadReviewProgressFromDocumentSettings() ?? (await loadReviewProgress());

    const progress: ReviewProgress = {
      ...reviewProgress,
      version: STORAGE_VERSION,
      documentId,
      documentName,
      lastSaved: new Date().toISOString(),
      boundaryIndices:
        boundaryIndices !== undefined ? boundaryIndices : reviewProgress?.boundaryIndices,
      outlineMode: outlineMode !== undefined ? outlineMode : reviewProgress?.outlineMode,
      profileName: profileName !== undefined ? profileName : reviewProgress?.profileName,
      selectedCharacterStyleFilter:
        selectedCharacterStyleFilter !== undefined
          ? selectedCharacterStyleFilter
          : reviewProgress?.selectedCharacterStyleFilter,
      selectedCharacterOriginFilter:
        selectedCharacterOriginFilter !== undefined
          ? selectedCharacterOriginFilter
          : reviewProgress?.selectedCharacterOriginFilter,
      selectedCharacterStyleId:
        selectedCharacterStyleId !== undefined
          ? selectedCharacterStyleId
          : reviewProgress?.selectedCharacterStyleId,
      selectedGroupFilter:
        selectedGroupFilter !== undefined
          ? selectedGroupFilter
          : reviewProgress?.selectedGroupFilter,
      selectedIndices:
        selectedIndices !== undefined ? selectedIndices : reviewProgress?.selectedIndices,
      selectedStyleFilter:
        selectedStyleFilter !== undefined
          ? selectedStyleFilter
          : reviewProgress?.selectedStyleFilter,
      total: total !== undefined ? total : reviewProgress?.total,
    };

    // In memory now so the next merge and any reader see it; written to disk once saves go quiet.
    setDocumentSetting(REVIEW_PROGRESS_SETTING_KEY, progress);
    schedulePersist(`${STORAGE_KEY_PREFIX}${documentId}`, JSON.stringify(progress));
  } catch (err) {
    console.warn("Failed to save review progress:", err);
    // Don't throw - progress saving should not interrupt workflow
  }
}

const PERSIST_DELAY_MS = 300;
let persistTimer: ReturnType<typeof setTimeout> | null = null;
let pendingPersist: { key: string; json: string } | null = null;
let persistQueue: Promise<void> = Promise.resolve();

function schedulePersist(key: string, json: string): void {
  pendingPersist = { key, json };
  if (persistTimer !== null) clearTimeout(persistTimer);
  persistTimer = setTimeout(() => {
    persistTimer = null;
    persistQueue = persistQueue.then(flushPendingPersist);
  }, PERSIST_DELAY_MS);
}

function cancelPendingPersist(): void {
  if (persistTimer !== null) clearTimeout(persistTimer);
  persistTimer = null;
  pendingPersist = null;
}

async function flushPendingPersist(): Promise<void> {
  const pending = pendingPersist;
  pendingPersist = null;
  if (!pending) return;
  try {
    await storage.setItem(pending.key, pending.json);
    await saveDocumentSettings();
  } catch (err) {
    console.warn("Failed to persist review progress:", err);
  }
}

/**
 * Loads review progress (filters/position) saved into the document file.
 *
 * Unlike `loadReviewProgress`, this reads synchronously from document
 * settings already loaded into memory — used to restore filters/position
 * when entering the Review page.
 */
export function loadReviewProgressFromDocumentSettings(): ReviewProgress | null {
  const progress = loadDocumentSetting<ReviewProgress>(REVIEW_PROGRESS_SETTING_KEY);
  if (!progress || progress.version !== STORAGE_VERSION) {
    return null;
  }
  return progress;
}

/**
 * Load review progress from localStorage for current document.
 * Optimized with cached document ID lookup and streamlined version migration.
 */
export async function loadReviewProgress(): Promise<ReviewProgress | null> {
  try {
    const documentId = await getDocumentIdentifier();
    const key = `${STORAGE_KEY_PREFIX}${documentId}`;
    const stored = await storage.getItem(key);

    if (!stored) {
      return null;
    }

    const progress = JSON.parse(stored) as ReviewProgress;

    // Version check - clear old versions, ignore future versions
    if (progress.version !== STORAGE_VERSION) {
      if (progress.version < STORAGE_VERSION) {
        console.warn(
          `Review progress version ${progress.version} is outdated (current: ${STORAGE_VERSION}), clearing saved state`
        );
        await clearReviewProgress();
      } else {
        console.warn(
          `Review progress version ${progress.version} is newer than supported (current: ${STORAGE_VERSION}), ignoring`
        );
      }
      return null;
    }

    return progress;
  } catch (err) {
    console.warn("Failed to load review progress:", err);
    return null;
  }
}

/**
 * Clear saved progress for current document.
 * Optimized with cached document ID lookup.
 */
export async function clearReviewProgress(): Promise<void> {
  // A pending or in-flight write would otherwise restore the progress just cleared.
  cancelPendingPersist();
  await persistQueue;
  try {
    const documentId = await getDocumentIdentifier();
    const key = `${STORAGE_KEY_PREFIX}${documentId}`;
    await storage.removeItem(key);
    await removeDocumentSetting(REVIEW_PROGRESS_SETTING_KEY);
  } catch (err) {
    console.warn("Failed to clear review progress:", err);
  }
}

/**
 * Get all saved review sessions (for debugging/cleanup).
 * Optimized to fetch all keys and values efficiently.
 */
export async function getAllSavedProgress(): Promise<ReviewProgress[]> {
  const results: ReviewProgress[] = [];
  const keys = await storage.getAllKeys();

  for (const key of keys) {
    if (key.startsWith(STORAGE_KEY_PREFIX)) {
      try {
        const value = await storage.getItem(key);
        if (value) {
          results.push(JSON.parse(value));
        }
      } catch {
        // Skip invalid entries
      }
    }
  }

  return results;
}

/**
 * Clean up old progress entries (older than 30 days).
 * Optimized to collect keys first, then remove in batch.
 */
export async function cleanupOldProgress(daysToKeep: number = 30): Promise<number> {
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
  const keysToRemove: string[] = [];
  const keys = await storage.getAllKeys();

  // Collect keys to remove
  for (const key of keys) {
    if (key.startsWith(STORAGE_KEY_PREFIX)) {
      try {
        const value = await storage.getItem(key);
        if (value) {
          const progress = JSON.parse(value) as ReviewProgress;
          const savedDate = new Date(progress.lastSaved);
          if (savedDate < cutoffDate) {
            keysToRemove.push(key);
          }
        }
      } catch {
        // Mark invalid entries for removal
        keysToRemove.push(key);
      }
    }
  }

  // Remove collected keys
  for (const key of keysToRemove) {
    await storage.removeItem(key);
  }

  return keysToRemove.length;
}
